import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { engravingExtraPerUnit } from "@/lib/engraving";

export interface CartItemPrivateLabel {
  enabled: boolean;
  brandName: string;
  logoFile?: File;
  logoPreview?: string;
  engravingId?: string;
  engravingLabel?: string;
  engravingPrice?: number; // por peça
  technique?: "laser" | "hotstamping";
  leadTimeDays?: number;
  comboFlanela?: boolean;
}

export interface CartItem {
  productId: string;
  name: string;
  reference: string;
  color: string;
  imageUrl: string;
  price: number;
  quantity: number;
  notes?: string;
  category?: string;
  privateLabel: CartItemPrivateLabel;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  addItem: (item: Omit<CartItem, "quantity" | "privateLabel">, quantity?: number, privateLabel?: CartItemPrivateLabel) => void;
  removeItem: (productId: string, color: string) => void;
  updateQuantity: (productId: string, color: string, quantity: number) => void;
  updatePrivateLabel: (productId: string, color: string, label: CartItemPrivateLabel) => void;
  applyLogoToAll: (brandName: string, logoFile?: File, logoPreview?: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType>({
  items: [],
  itemCount: 0,
  total: 0,
  addItem: () => {},
  removeItem: () => {},
  updateQuantity: () => {},
  updatePrivateLabel: () => {},
  applyLogoToAll: () => {},
  clearCart: () => {},
});

const CART_KEY = "atacado_optico_cart";

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const serializable = items.map(({ privateLabel, ...rest }) => ({
      ...rest,
      privateLabel: {
        enabled: privateLabel.enabled,
        brandName: privateLabel.brandName,
        logoPreview: privateLabel.logoPreview,
        engravingId: privateLabel.engravingId,
        engravingLabel: privateLabel.engravingLabel,
        engravingPrice: privateLabel.engravingPrice,
        technique: privateLabel.technique,
        leadTimeDays: privateLabel.leadTimeDays,
        comboFlanela: privateLabel.comboFlanela,
      },
    }));
    localStorage.setItem(CART_KEY, JSON.stringify(serializable));
  }, [items]);

  const addItem = (item: Omit<CartItem, "quantity" | "privateLabel">, quantity = 1, privateLabel?: CartItemPrivateLabel) => {
    const qty = Math.max(1, Math.floor(quantity));
    const pl: CartItemPrivateLabel = privateLabel ?? { enabled: false, brandName: "" };
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === item.productId && i.color === item.color);
      if (existing) {
        return prev.map((i) =>
          i.productId === item.productId && i.color === item.color
            ? { ...i, quantity: i.quantity + qty, privateLabel: privateLabel ? pl : i.privateLabel }
            : i
        );
      }
      return [...prev, { ...item, quantity: qty, privateLabel: pl }];
    });
  };

  const removeItem = (productId: string, color: string) => {
    setItems((prev) => prev.filter((i) => !(i.productId === productId && i.color === color)));
  };

  const updateQuantity = (productId: string, color: string, quantity: number) => {
    if (quantity <= 0) return removeItem(productId, color);
    setItems((prev) =>
      prev.map((i) => (i.productId === productId && i.color === color ? { ...i, quantity } : i))
    );
  };

  const updatePrivateLabel = (productId: string, color: string, label: CartItemPrivateLabel) => {
    setItems((prev) =>
      prev.map((i) => (i.productId === productId && i.color === color ? { ...i, privateLabel: label } : i))
    );
  };

  const applyLogoToAll = (brandName: string, logoFile?: File, logoPreview?: string) => {
    setItems((prev) =>
      prev.map((i) => ({
        ...i,
        privateLabel: { enabled: true, brandName, logoFile, logoPreview },
      }))
    );
  };

  const clearCart = () => setItems([]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce(
    (sum, i) => sum + i.price * i.quantity + engravingExtraPerUnit(i.privateLabel) * i.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{ items, itemCount, total, addItem, removeItem, updateQuantity, updatePrivateLabel, applyLogoToAll, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
